import {
  BillLinReceivableAddressSchema,
  BillLinReceivableSchema,
  BillLinReceivableSlipSchema,
} from "@aera/data-table";
import type {
  ReceivableKind,
  ReceivableListItem,
} from "@/lib/pendingOrders";
import { filterReceivableFieldsForDisplay } from "@/lib/pendingOrders";

const SECTION_LABELS_TH: Record<string, string> = {
  PAYMENT: "การชำระเงิน",
  CUSTOMER: "ลูกค้า",
  SHIPPING: "จัดส่ง",
  DETAILS: "รายละเอียด",
};

export function isMultilineReceivableField(key: string, value: string) {
  return (
    value.includes("\n") ||
    value.length > 48 ||
    ["address_text", "address", "remark"].includes(key)
  );
}

export type ReceivableDrawerSection = {
  label: string;
  title: string;
  fields: ReceivableListItem["fields"];
};

export function groupReceivableDrawerFields(
  fields: ReceivableListItem["fields"],
  kind?: ReceivableKind,
): ReceivableDrawerSection[] {
  const schema =
    kind === "slip"
      ? BillLinReceivableSlipSchema
      : kind === "address"
        ? BillLinReceivableAddressSchema
        : BillLinReceivableSchema;
  const displayFields = filterReceivableFieldsForDisplay(fields, kind);
  const available = new Map(displayFields.map((field) => [field.key, field]));
  const used = new Set<string>();
  const sections: ReceivableDrawerSection[] = [];

  for (const section of schema.drawerSections) {
    const sectionFields = section.fields
      .map((fieldId) => available.get(fieldId))
      .filter((field): field is ReceivableListItem["fields"][number] => {
        if (!field) return false;
        used.add(field.key);
        return true;
      });

    if (sectionFields.length === 0) continue;

    sections.push({
      label: section.label,
      title: SECTION_LABELS_TH[section.label] ?? section.label,
      fields: sectionFields,
    });
  }

  const remaining = displayFields.filter((field) => !used.has(field.key));
  if (remaining.length > 0) {
    const details = sections.find((section) => section.label === "DETAILS");
    if (details) {
      details.fields.push(...remaining);
    } else {
      sections.push({
        label: "DETAILS",
        title: SECTION_LABELS_TH.DETAILS,
        fields: remaining,
      });
    }
  }

  return sections;
}

const RECEIVABLE_AMOUNT_FIELD_KEYS = new Set([
  "slip_amount",
  "amount",
  "amount_original",
  "amount_allocated",
  "amount_paid",
  "amount_remaining",
  "debt_amount",
  "debt",
  "balance",
  "total",
  "outstanding_amount",
  "outstanding",
  "price",
  "total_amount",
  "transfer_amount",
  "money",
]);

const RECEIVABLE_PHONE_FIELD_KEYS = new Set([
  "addr_phone",
  "phone",
  "recipient_phone",
  "contact_phone",
]);

const RECEIVABLE_BANK_FIELD_KEYS = new Set([
  "bank",
  "bank_name",
  "sender_bank",
  "ธนาคาร",
]);

const RECEIVABLE_DATE_FIELD_KEYS = new Set([
  "created_at",
  "updated_at",
  "recorded_at",
  "record_date",
  "transfer_date",
  "transfer_datetime",
  "slip_date",
  "payment_date",
]);

const RECEIVABLE_REFERENCE_FIELD_KEYS = new Set([
  "slip_ref",
  "reference_no",
  "reference",
]);

const RECEIVABLE_IMAGE_FIELD_KEYS = new Set([
  "slip_image",
  "image_url",
  "image",
  "file_url",
  "slip_url",
]);

export type ReceivableFieldCellType =
  | "amount"
  | "datetime"
  | "expiry"
  | "bank"
  | "tel"
  | "reference"
  | "image"
  | "text";

export function receivableFieldCellType(key: string): ReceivableFieldCellType {
  if (key === "expires_at") return "expiry";
  if (RECEIVABLE_IMAGE_FIELD_KEYS.has(key)) return "image";
  if (RECEIVABLE_AMOUNT_FIELD_KEYS.has(key)) return "amount";
  if (RECEIVABLE_DATE_FIELD_KEYS.has(key)) return "datetime";
  if (RECEIVABLE_BANK_FIELD_KEYS.has(key)) return "bank";
  if (RECEIVABLE_PHONE_FIELD_KEYS.has(key)) return "tel";
  if (RECEIVABLE_REFERENCE_FIELD_KEYS.has(key)) return "reference";
  return "text";
}

/**
 * Generic detail inputs without a save API must stay read-only.
 * Money / customer name use dedicated correction editors instead.
 */
const RECEIVABLE_EDITABLE_FIELD_KEYS = new Set<string>([]);

export function isReceivableFieldEditable(fieldKey: string): boolean {
  return RECEIVABLE_EDITABLE_FIELD_KEYS.has(fieldKey);
}

export function resolveReceivableKindFromItem(
  item: ReceivableListItem,
): ReceivableKind {
  const label = item.kindLabel ?? "";
  if (label.includes("สลิป")) return "slip";
  if (label.includes("ที่อยู่")) return "address";
  return "announcement";
}
